import type { Character } from "../types/character";
import type { CharacterRanking } from "../types/common";
import type { Player } from "../types/player";

const SOLE_RANK_GARNETS: Readonly<Record<number, number>> = {
  1: 3,
  2: 2,
  3: 1,
};

function countCharactersAtRank(
  rankings: readonly CharacterRanking[],
  rank: number,
): number {
  return rankings.filter((r) => r.rank === rank).length;
}

function getTiedGarnets(rank: number, tiedCount: number): number {
  if (rank > 3) return 0;
  if (rank === 1) {
    // 1st is always unique in normal play (sequential resolve stops on finish);
    // this only applies to the pool-exhaustion edge with no finisher.
    return tiedCount === 2 ? 2 : 1;
  }
  if (rank === 2) return 1; // tied 2nd → 1 each (official)
  return 0; // tied 3rd → 0 (official)
}

function getGarnetsForRanking(
  ranking: CharacterRanking,
  rankings: readonly CharacterRanking[],
): number {
  const tiedCount = countCharactersAtRank(rankings, ranking.rank);
  if (tiedCount === 1) {
    return SOLE_RANK_GARNETS[ranking.rank] ?? 0;
  }
  return getTiedGarnets(ranking.rank, tiedCount);
}

export function calculateGarnetsFromRace(
  rankings: readonly CharacterRanking[],
  players: readonly Player[],
): Readonly<Record<string, number>> {
  const characterGarnets = new Map<Character, number>();
  for (const ranking of rankings) {
    const garnets = getGarnetsForRanking(ranking, rankings);
    characterGarnets.set(ranking.character, garnets);
  }

  const playerGarnets: Record<string, number> = {};
  for (const player of players) {
    let total = 0;
    if (player.secretSelection !== null) {
      for (const character of player.secretSelection) {
        total += characterGarnets.get(character) ?? 0;
      }
    }
    playerGarnets[player.id] = total;
  }

  return playerGarnets;
}

export function mergeGarnets(
  existing: Readonly<Record<string, number>>,
  additional: Readonly<Record<string, number>>,
): Readonly<Record<string, number>> {
  const merged: Record<string, number> = { ...existing };
  for (const [playerId, garnets] of Object.entries(additional)) {
    merged[playerId] = (merged[playerId] ?? 0) + garnets;
  }
  return merged;
}

export function determineWinner(
  garnets: Readonly<Record<string, number>>,
): string | null {
  const entries = Object.entries(garnets);
  if (entries.length === 0) {
    return null;
  }

  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  if (sorted.length > 1 && sorted[0][1] === sorted[1][1]) {
    return null; // Tie
  }

  return sorted[0][0];
}
